import { rupees } from "../rates/rateModel";
import type { T } from "../i18n/kt";
import type { DayCash, Money } from "./types";

/** Money strings from the server as numbers (paise kept); anything unreadable is 0. */
export const num = (m: Money | number | null | undefined): number => {
  if (m === null || m === undefined || m === "") return 0;
  const x = typeof m === "number" ? m : Number(m);
  return isFinite(x) ? x : 0;
};

/** Sum of money strings, rounded to paise. */
export const addMoney = (...xs: (Money | number | null | undefined)[]): number => Math.round(xs.reduce<number>((s, x) => s + num(x), 0) * 100) / 100;

export type SumPart = { sign: 1 | -1; key: string; amount: number };

/**
 * The line under the big "cash in hand" number (V1_SPEC D18, 11.4 A2): only the non-zero parts, in
 * the order the money moved. Paid on a sheet not yet checked (provisional, D8) counts as paid today:
 * for the supervisor it is cash that left his hand. Incoming transfers are never in it.
 */
export const sumParts = (c: DayCash): SumPart[] => {
  const parts: SumPart[] = [
    { sign: 1, key: "Carried from yesterday {{amount}}", amount: num(c.opening) },
    { sign: 1, key: "received today {{amount}}", amount: num(c.handoverIn) },
    { sign: 1, key: "received from others {{amount}}", amount: num(c.transferIn) },
    { sign: -1, key: "paid today {{amount}}", amount: addMoney(c.paid, c.provisional) },
    { sign: -1, key: "spent {{amount}}", amount: num(c.spent) },
    { sign: -1, key: "given back {{amount}}", amount: num(c.returned) },
    { sign: -1, key: "handed to others {{amount}}", amount: num(c.transferOut) },
  ];
  return parts
    .filter((p) => p.amount !== 0)
    .map((p) => (p.amount < 0 ? { ...p, sign: (p.sign === 1 ? -1 : 1) as 1 | -1, amount: -p.amount } : p));
};

/** "कल का ₹1,200 + आज मिला ₹30,000 − आज दिया ₹26,400 − खर्च ₹240"; "" when nothing moved. */
export const sumLine = (c: DayCash, t: T): string =>
  sumParts(c)
    .map((p, i) => {
      const text = t(p.key, { amount: rupees(p.amount) });
      if (i === 0) return p.sign === 1 ? text : `− ${text}`;
      return `${p.sign === 1 ? "+" : "−"} ${text}`;
    })
    .join(" ");

const IST = "Asia/Kolkata";

/** "6:10 pm" in India time. */
export const fmtTime = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: IST }).replace(/\s?(AM|PM)$/i, (m) => ` ${m.trim().toLowerCase()}`);
};

/** "2 Nov, 9:10 am" in India time. */
export const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: IST });
  return `${day}, ${fmtTime(iso)}`;
};

/** Today in India as yyyy-MM-dd (entries belong to the paper date, V1_SPEC section 3). */
export const istToday = (now: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: IST, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  return parts; // en-CA formats as 2026-11-02
};

/** "2 नवंबर" / "2 Nov" for a yyyy-MM-dd date. */
export const fmtShortDate = (iso: string, lang: "en" | "hi"): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: lang === "hi" ? "long" : "short" });
};

/** "2, 3" for a list of page numbers. */
export const pageList = (pages: number[]): string => pages.join(", ");

/** Server messages are English (V1_SPEC section 9); known codes get the screen's own wording. */
export const CODE_TEXT: Record<string, string> = {
  CHANGED_MEANWHILE: "Someone changed this just now. Refresh and try again.",
  CHANGE_PENDING: "A change is already waiting for this.",
  USE_CHANGE_REQUEST: "This is checked. Ask for a change instead.",
  ALREADY_VERIFIED: "This is checked. Ask for a change instead.",
  NOTHING_TO_PRINT: "Everyone on this sheet is paid. Nothing to print.",
  PHOTO_TOO_BIG: "The photo is too big. Take it again.",
  KEY_INVALID: "The photo didn't upload. Try again.",
  STORAGE_NOT_SET: "Photo storage isn't set up yet. Ask HKP support.",
  HEAD_NOT_FOUND: "Choose a spend type.",
  REASON_REQUIRED: "Say what happened",
};

/** The text to show for a refused call: a known code's wording, else the server's message. */
export const refusalText = (res: { code?: string; message?: string } | null | undefined, t: T): string => {
  const code = res?.code || "";
  if (CODE_TEXT[code]) return t(CODE_TEXT[code]);
  return t(res?.message || "Not saved. Check the network and retry.");
};
